import { Injectable } from '@angular/core';
import { UserRemoteControllerService } from 'core/api';
import { IUserDto } from 'core/dto';
import { RemoteItemService, RemoteItemStore } from 'core/remotes';
import { mapKey } from 'core/rx-js';
import { LoaderService, SnackbarService } from 'core/services';
import { SplugConfigService } from 'core/splug';
import { DsgDeepTranslationKey } from 'projects/designer/src/app/pipes/translate.pipe';
import { take } from 'rxjs';
import applyEntityLoader from '../../../sign-process-wizard/remotes/utils/apply-entity-loader';


const userEntityLoader = applyEntityLoader('user')

@Injectable()
export class RemoteUserService extends RemoteItemService<IUserDto> {

  get userId() {
    return this.keyValueSubject.value;
  }
  readonly userSubject = this.localItemStore.stateSubject;


  constructor(
    private readonly _userRemoteControllerService: UserRemoteControllerService,
    private readonly _splugConfigService: SplugConfigService,
    private readonly loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly snackbarService: SnackbarService<DsgDeepTranslationKey>
  ) {
    super();
    super.setConfig({
      customStore: new RemoteItemStore({
        keyField: 'id',
        insert: (userDto) => userEntityLoader({
          loaderService,
          methodKey: 'insert',
          obs: this._userRemoteControllerService.create(userDto)
            .pipe(
              mapKey('id'),
              snackbarService.handleAsync({
                errorMessage: 'ss-entities.user.insert.error',
                successMessage: 'ss-entities.user.insert.success'
              }),

            ),
          text: 'ss-entities.user.insert.pending'
        }),
        update: (id, userDto) => userEntityLoader({
          loaderService,
          methodKey: 'update',
          text: 'ss-entities.user.update.display',
          obs: this._userRemoteControllerService.update(id, userDto)
            .pipe(
              snackbarService.handleAsync({
                errorMessage: 'ss-entities.user.update.error',
                successMessage: 'ss-entities.user.update.success'
              })
            )
        }
        ),
        load: (id) => userEntityLoader({
          loaderService,
          methodKey: 'load',
          text: 'ss-entities.user.load.pending',
          obs: this._userRemoteControllerService.getOne(id)
            .pipe(
              snackbarService.handleError({
                message: 'ss-entities.user.remove.error',
                type: 'error'
              })
            )
        }),
        remove: (id) => userEntityLoader({
          loaderService,
          methodKey: 'remove',
          text: 'ss-entities.user.remove.title',
          obs: this._userRemoteControllerService.remove(id)
            .pipe(
              snackbarService.handleAsync({
                errorMessage: 'ss-entities.user.remove.error',
                successMessage: 'ss-entities.user.remove.success'
              })
            )
        }),
      }),
      map: (userDto) => {

        if (!userDto) {
          return null
        }

        return {
          ...userDto,
          sigConfigs: userDto.sigConfigs ? userDto.sigConfigs.map((config) => ({
            ...config,
            params: Object.entries(config.params)
              .reduce<any>((total, [key, value]) => {
                total[key] = _splugConfigService.executeParser(config.sigTypeId, key, value);
                return total
              }, {}) as any
          })) : null
        }
      },
      unMap: (user) => {

        if (!user) {
          return null;
        }

        return {
          ...user,
          sigConfigs: user.sigConfigs ? user.sigConfigs.map((config) => ({
            ...config,
            params: Object.entries(config.params)
              .reduce<any>((total, [key, value]) => {
                total[key] = _splugConfigService.executeStringifer(config.sigTypeId, key, value);
                return total
              }, {}) as any
          })) : null
        }
      }
    })




  }


  changePassword(userId: string, newPassword: string) {

    const id = this.loaderService.show({ key: 'user.change-password', text: 'ss-entities.user.change-password.pending' })
    this._userRemoteControllerService.changePassword(userId, newPassword)
      .pipe(
        take(1),
        this.loaderService.disposeLoader(id),
        this.snackbarService.handleAsync({ errorMessage: 'ss-entities.user.change-password.error', successMessage: 'ss-entities.user.change-password.success' }),
      )
  }
}
